import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Button from '../components/ui/button';

const PricingContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 6rem 1rem;
`;

const PageTitle = styled(motion.h1)`
  font-size: 3rem;
  margin-bottom: 1rem;
  text-align: center;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 2.5rem;
  }
`;

const PageSubtitle = styled(motion.p)`
  font-size: 1.25rem;
  color: ${({ theme }) => theme.colors.gray};
  margin-bottom: 3rem;
  text-align: center;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
`;

const BillingToggle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 3rem;
`;

const BillingOption = styled.button<{ isActive: boolean }>`
  padding: 0.5rem 1rem;
  font-size: 1rem;
  font-weight: ${({ isActive }) => (isActive ? '600' : '400')};
  color: ${({ isActive, theme }) => (isActive ? theme.colors.primary : theme.colors.gray)};
  background: none;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const ToggleSwitch = styled.div`
  position: relative;
  width: 60px;
  height: 30px;
  border-radius: 15px;
  background-color: ${({ theme }) => theme.colors.primary};
  margin: 0 1rem;
  cursor: pointer;
`;

const ToggleKnob = styled.div<{ isYearly: boolean }>`
  position: absolute;
  top: 3px;
  left: ${({ isYearly }) => (isYearly ? '33px' : '3px')};
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: white;
  transition: left 0.2s ease;
`;

const SaveBadge = styled.span`
  background-color: ${({ theme }) => theme.colors.secondary};
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.5rem;
  border-radius: ${({ theme }) => theme.radii.full};
  margin-left: 0.5rem;
`;

const PricingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    max-width: 500px;
    margin: 0 auto;
  }
`;

const PricingCard = styled(motion.div)<{ isPopular?: boolean }>`
  background-color: white;
  border-radius: ${({ theme }) => theme.radii.lg};
  padding: 2.5rem 2rem;
  box-shadow: ${({ theme }) => theme.shadows.md};
  display: flex;
  flex-direction: column;
  position: relative;
  border: ${({ isPopular, theme }) => (isPopular ? `2px solid ${theme.colors.primary}` : '1px solid ' + theme.colors.grayLight)};
  
  ${({ isPopular }) => isPopular && `
    transform: scale(1.05);
    
    @media (max-width: 1024px) {
      transform: scale(1);
    }
  `}
`;

const PopularBadge = styled.div`
  position: absolute;
  top: -12px;
  right: 20px;
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 1rem;
  border-radius: ${({ theme }) => theme.radii.full};
`;

const PlanName = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
`;

const PlanDescription = styled.p`
  color: ${({ theme }) => theme.colors.gray};
  margin-bottom: 2rem;
  font-size: 0.875rem;
`;

const PriceContainer = styled.div`
  margin-bottom: 2rem;
`;

const Price = styled.div`
  font-size: 3rem;
  font-weight: 700;
  display: flex;
  align-items: baseline;
  
  span {
    font-size: 1rem;
    font-weight: 400;
    margin-left: 0.25rem;
    color: ${({ theme }) => theme.colors.gray};
  }
`;

const BillingPeriod = styled.div`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.gray};
`;

const FeaturesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 2rem;
  flex-grow: 1;
`;

const Feature = styled.li`
  font-size: 0.875rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: flex-start;
  
  &::before {
    content: '✓';
    color: ${({ theme }) => theme.colors.secondary};
    font-weight: bold;
    margin-right: 0.75rem;
  }
`;

const PricingCta = styled.div`
  margin-top: auto;
`;

const EnterpriseCard = styled.div`
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary} 0%, ${({ theme }) => theme.colors.primaryDark} 100%);
  border-radius: ${({ theme }) => theme.radii.lg};
  padding: 3rem 2rem;
  color: white;
  margin-top: 4rem;
  text-align: center;
`;

const EnterpriseTitle = styled.h3`
  font-size: 2rem;
  margin-bottom: 1rem;
`;

const EnterpriseDescription = styled.p`
  font-size: 1.125rem;
  margin-bottom: 2rem;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  opacity: 0.9;
`;

interface PricingPlan {
  name: string;
  description: string;
  monthlyPrice: number;
  yearlyPrice: number;
  features: string[];
  isPopular?: boolean;
  buttonText: string;
  buttonVariant: 'primary' | 'secondary' | 'outline';
}

const PricingPage: React.FC = () => {
  const [isYearly, setIsYearly] = useState<boolean>(true);
  
  useEffect(() => {
    document.title = 'Pricing | FylePrep';
  }, []);

  const pricingPlans: PricingPlan[] = [
    {
      name: 'Starter',
      description: 'Perfect for small businesses just beginning their data journey',
      monthlyPrice: 49,
      yearlyPrice: 39,
      features: [
        'Connect up to 3 data sources',
        'Basic data visualization',
        'Weekly automated reports',
        'Email support',
        '5GB data storage',
        'Up to 3 team members'
      ],
      buttonText: 'Start Free Trial',
      buttonVariant: 'outline'
    },
    {
      name: 'Professional',
      description: 'Ideal for growing businesses with increasing data needs',
      monthlyPrice: 99,
      yearlyPrice: 79,
      features: [
        'Connect up to 10 data sources',
        'Advanced data visualization',
        'Daily automated reports',
        'Priority email & chat support',
        '25GB data storage',
        'Up to 10 team members',
        'Custom dashboard creation',
        'API access'
      ],
      isPopular: true,
      buttonText: 'Start Free Trial',
      buttonVariant: 'primary'
    },
    {
      name: 'Business',
      description: 'For established businesses with complex data requirements',
      monthlyPrice: 199,
      yearlyPrice: 159,
      features: [
        'Unlimited data sources',
        'Advanced analytics & ML models',
        'Real-time data processing',
        '24/7 priority support',
        '100GB data storage',
        'Up to 25 team members',
        'Custom dashboard creation',
        'API access',
        'Dedicated account manager',
        'Custom integrations'
      ],
      buttonText: 'Start Free Trial',
      buttonVariant: 'secondary'
    }
  ];

  const toggleBillingCycle = (): void => {
    setIsYearly(!isYearly);
  };

  return (
    <PricingContainer>
      <PageTitle
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        Simple, Transparent Pricing
      </PageTitle>
      
      <PageSubtitle
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        Choose the plan that fits your business needs. All plans include a 14-day free trial.
        No credit card required to start.
      </PageSubtitle>
      
      <BillingToggle>
        <BillingOption isActive={!isYearly} onClick={() => setIsYearly(false)}>
          Monthly
        </BillingOption>
        
        <ToggleSwitch onClick={toggleBillingCycle}>
          <ToggleKnob isYearly={isYearly} />
        </ToggleSwitch>
        
        <BillingOption isActive={isYearly} onClick={() => setIsYearly(true)}>
          Yearly
          <SaveBadge>Save 20%</SaveBadge>
        </BillingOption>
      </BillingToggle>
      
      <PricingGrid>
        {pricingPlans.map((plan, index) => (
          <PricingCard 
            key={index}
            isPopular={plan.isPopular}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
          >
            {plan.isPopular && <PopularBadge>Most Popular</PopularBadge>}
            
            <PlanName>{plan.name}</PlanName>
            <PlanDescription>{plan.description}</PlanDescription>
            
            <PriceContainer>
              <Price>
                ${isYearly ? plan.yearlyPrice : plan.monthlyPrice}
                <span>/month</span>
              </Price>
              <BillingPeriod>
                {isYearly ? 'Billed annually' : 'Billed monthly'}
              </BillingPeriod>
            </PriceContainer>
            
            <FeaturesList>
              {plan.features.map((feature, featureIndex) => (
                <Feature key={featureIndex}>{feature}</Feature>
              ))}
            </FeaturesList>
            
            <PricingCta>
              <Button 
                variant={plan.buttonVariant} 
                fullWidth
              >
                {plan.buttonText}
              </Button>
            </PricingCta>
          </PricingCard>
        ))}
      </PricingGrid>
      
      <EnterpriseCard>
        <EnterpriseTitle>Need a Custom Solution?</EnterpriseTitle>
        <EnterpriseDescription>
          We offer tailored enterprise plans for organizations with specific requirements.
          Get in touch with our team to discuss your needs.
        </EnterpriseDescription>
        <Button variant="secondary" size="large">Contact Sales</Button>
      </EnterpriseCard>
    </PricingContainer>
  );
};

export default PricingPage;
